import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeletePaymentDialog } from "@/components/finance/DeletePaymentDialog";

export const InvoicePaymentsDialog = ({ invoice, onOpenChange, onChanged }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    if (!invoice?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/mgmt/payments", { params: { invoice_id: invoice.id } });
      setPayments(data || []);
    } catch (error) { toast.error(formatErr(error.response?.data?.detail) || error.message); }
    finally { setLoading(false); }
  }, [invoice?.id]);

  useEffect(() => { if (invoice) load(); }, [invoice, load]);

  return (
    <>
      <Dialog open={Boolean(invoice)} onOpenChange={onOpenChange}>
        <DialogContent closeTestId="invoice-payments-close" className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="invoice-payments-dialog">
          <DialogHeader><DialogTitle>Undo payment · {invoice?.number}</DialogTitle><DialogDescription>Select the exact payment to permanently delete. The invoice unpaid balance will increase by that amount.</DialogDescription></DialogHeader>
          {loading ? <div className="py-10 flex justify-center" data-testid="invoice-payments-loading"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            <div className="divide-y divide-border border border-border rounded-sm" data-testid="invoice-payments-list">
              {payments.length === 0 ? <p className="p-5 text-sm text-muted-foreground text-center">No recorded payments found.</p> : payments.map((payment) => (
                <div key={payment.id} className="p-3 flex items-center justify-between gap-3" data-testid={`invoice-payment-row-${payment.id}`}>
                  <div><div className="font-semibold mono-num">{fmtMoney(payment.amount)}</div><div className="text-xs text-muted-foreground">{payment.paid_at || "No date"} · {payment.payment_method || "Method not specified"}</div>{payment.payment_reference && <div className="text-[11px] text-muted-foreground">Ref: {payment.payment_reference}</div>}</div>
                  <Button type="button" size="icon" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleting(payment)} aria-label={`Delete ${fmtMoney(payment.amount)} payment from ${invoice?.number}`} data-testid={`invoice-payment-delete-${payment.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="invoice-payments-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <DeletePaymentDialog payment={deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }} onDeleted={() => { setDeleting(null); load(); onChanged?.(); }} />
    </>
  );
};